import React from 'react';
import { useSelector } from 'react-redux';

import { FormattedMessage } from '../../util/reactIntl';
import { NamedLink } from '..';

import { computeSellerStatus, STEP_STATE } from './sellerStatus';
import css from './SellerStatusPanel.module.css';

const STATE_ICONS = {
  [STEP_STATE.DONE]: '✓',
  [STEP_STATE.ACTION_NEEDED]: '!',
  [STEP_STATE.WAITING]: '…',
  [STEP_STATE.UNKNOWN]: '?',
};

const STATE_CLASSES = {
  [STEP_STATE.DONE]: css.done,
  [STEP_STATE.ACTION_NEEDED]: css.actionNeeded,
  [STEP_STATE.WAITING]: css.waiting,
  [STEP_STATE.UNKNOWN]: css.unknown,
};

/**
 * Seller status, shown at the top of the seller's own listings page.
 *
 * A seller with a verified email and nothing else can otherwise sit on an
 * empty listings page indefinitely with no indication that anything is
 * outstanding -- an unconnected Stripe account silently turns "Publish" into
 * a modal, and an account awaiting approval has no visible state at all. This
 * panel names whatever is actually in the way, and links to it.
 */
export default function SellerStatusPanel({ currentUser, listings, listingsLoaded }) {
  const stripeAccount = useSelector(state => state.stripeConnectAccount?.stripeAccount);

  if (!currentUser?.id) return null;

  const { steps, headline } = computeSellerStatus({
    currentUser,
    listings,
    listingsLoaded,
    stripeAccount,
  });

  return (
    <section className={css.root}>
      <p className={css.headline}>
        <FormattedMessage id={`SellerStatusPanel.headline.${headline}`} />
      </p>
      <ul className={css.steps}>
        {steps.map(step => {
          const stepDetail = step.detail ? `.${step.detail}` : '';
          return (
            <li key={step.id} className={css.step}>
              <span className={`${css.marker} ${STATE_CLASSES[step.state]}`} aria-hidden="true">
                {STATE_ICONS[step.state]}
              </span>
              <span className={css.stepText}>
                <span className={css.stepLabel}>
                  <FormattedMessage id={`SellerStatusPanel.step.${step.id}`} />
                </span>
                <span className={css.stepStatus}>
                  <FormattedMessage
                    id={`SellerStatusPanel.status.${step.id}.${step.state}${stepDetail}`}
                  />
                </span>
              </span>
              {step.actionRoute ? (
                <NamedLink className={css.stepAction} name={step.actionRoute}>
                  <FormattedMessage id={`SellerStatusPanel.action.${step.id}`} />
                </NamedLink>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className={css.footnote}>
        <FormattedMessage id="SellerStatusPanel.footnote" />
      </p>
    </section>
  );
}
